import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import WaveSurfer from 'wavesurfer.js';
import { api, ApiError, type VideoAudioResponse } from '../lib/api';

interface Props {
  videoId: string;
}

export function AudioPreview({ videoId }: Props) {
  const audioQuery = useQuery<VideoAudioResponse, ApiError>({
    queryKey: ['video-audio', videoId],
    queryFn: () => api.getVideoAudio(videoId),
    // Re-poll while narration is still being generated — backend returns 404 +
    // error code "narration_not_ready" in that state. TanStack Query treats it
    // as an error, so we let the user click retry rather than polling forever.
    retry: false,
  });

  if (audioQuery.isLoading) {
    return (
      <div className="card-neumorphic text-center text-parchment-700 py-12">
        <p className="font-mono text-sm">loading narration…</p>
      </div>
    );
  }

  if (audioQuery.isError) {
    const code = audioQuery.error.message;
    const isNotReady =
      code.includes('narration_not_ready') || code.includes('video_not_found');
    return (
      <div className="card-neumorphic text-center text-parchment-700 py-12">
        <p className="font-mono text-sm">
          {isNotReady ? 'narration not ready yet' : `audio fetch failed: ${code}`}
        </p>
        <p className="text-xs mt-2">
          {isNotReady
            ? 'voice + audio-aligner Lambdas produce this after script approval'
            : 'verify api is reachable and the video exists'}
        </p>
      </div>
    );
  }

  if (!audioQuery.data) return null;

  return <Waveform audio={audioQuery.data} />;
}

function Waveform({ audio }: { audio: VideoAudioResponse }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);

  useEffect(() => {
    if (!containerRef.current) return;

    const ws = WaveSurfer.create({
      container: containerRef.current,
      height: 96,
      waveColor: '#9ec2a4',
      progressColor: '#4a7c59',
      cursorColor: '#1b6b4a',
      cursorWidth: 2,
      barWidth: 2,
      barGap: 1,
      barRadius: 1,
      normalize: true,
    });

    ws.load(audio.narration_url);
    ws.on('ready', () => setReady(true));
    ws.on('play', () => setPlaying(true));
    ws.on('pause', () => setPlaying(false));
    ws.on('finish', () => setPlaying(false));
    ws.on('timeupdate', (t: number) => setPosition(t));

    wavesurferRef.current = ws;
    return () => {
      ws.destroy();
      wavesurferRef.current = null;
    };
  }, [audio.narration_url]);

  const onTogglePlay = () => {
    wavesurferRef.current?.playPause();
  };

  const formattedDuration = audio.narration_duration_sec
    ? formatTime(audio.narration_duration_sec)
    : '—';

  return (
    <div className="card-neumorphic">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-heading font-semibold">Narration Preview</h3>
          <p className="text-xs font-mono text-parchment-700 mt-1">
            video {audio.video_id} · {audio.chapter_audio_keys.length} chapters ·{' '}
            {formattedDuration}
          </p>
        </div>
        <button
          type="button"
          onClick={onTogglePlay}
          disabled={!ready}
          className="btn-primary disabled:opacity-50"
        >
          {playing ? 'Pause' : 'Play'}
        </button>
      </div>
      <div
        ref={containerRef}
        className="rounded-button bg-parchment-50 px-4 py-3 shadow-neumorphic-inset"
      />
      <p className="text-xs font-mono text-parchment-700 mt-3">
        position: {formatTime(position)} / {formattedDuration}
      </p>
    </div>
  );
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0');
  return `${m}:${s}`;
}
